import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { HttpParams } from '@angular/common/http';

import { BaseHttpService } from './base-http.service';
import {
  CalendarEventsResponse,
  CalendarEventCreate,
  CalendarEventUpdate,
  CalendarEventActionResponse,
  CalendarVisibleUsersResponse,
  CalendarVisibilityResponse,
} from '../interfaces/calendar-event.interface';

@Injectable({
  providedIn: 'root'
})
export class CalendarService extends BaseHttpService {

  // ─── Események ──────────────────────────────────

  getEvents(start: string, end: string): Observable<CalendarEventsResponse> {
    if (!this.hasValidToken()) {
      return this.returnErrorWhenNoToken();
    }
    const params = new HttpParams().set('start', start).set('end', end);
    return this.get<CalendarEventsResponse>('/calendar/events', params);
  }

  getSharedEvents(start: string, end: string, userIds: number[]): Observable<CalendarEventsResponse> {
    if (!this.hasValidToken()) {
      return this.returnErrorWhenNoToken();
    }
    const params = new HttpParams()
      .set('start', start)
      .set('end', end)
      .set('user_ids', userIds.join(','));
    return this.get<CalendarEventsResponse>('/calendar/shared-events', params);
  }

  createEvent(data: CalendarEventCreate): Observable<CalendarEventActionResponse> {
    if (!this.hasValidToken()) {
      return throwError(() => new Error('Nincs érvényes token'));
    }
    return this.post<CalendarEventActionResponse>('/calendar/events', data);
  }

  updateEvent(eventId: number, data: CalendarEventUpdate): Observable<CalendarEventActionResponse> {
    if (!this.hasValidToken()) {
      return throwError(() => new Error('Nincs érvényes token'));
    }
    return this.put<CalendarEventActionResponse>(`/calendar/events/${eventId}`, data);
  }

  deleteEvent(eventId: number): Observable<CalendarEventActionResponse> {
    if (!this.hasValidToken()) {
      return throwError(() => new Error('Nincs érvényes token'));
    }
    return this.delete<CalendarEventActionResponse>(`/calendar/events/${eventId}`);
  }

  // ─── Láthatóság ─────────────────────────────────

  getVisibleUsers(): Observable<CalendarVisibleUsersResponse> {
    if (!this.hasValidToken()) {
      return this.returnErrorWhenNoToken();
    }
    return this.get<CalendarVisibleUsersResponse>('/calendar/visible-users');
  }

  getAllVisibility(): Observable<CalendarVisibilityResponse> {
    if (!this.hasValidToken()) {
      return this.returnErrorWhenNoToken();
    }
    return this.get<CalendarVisibilityResponse>('/calendar/visibility');
  }

  grantVisibility(viewerUserId: number, targetUserId: number): Observable<CalendarEventActionResponse> {
    if (!this.hasValidToken()) {
      return throwError(() => new Error('Nincs érvényes token'));
    }
    return this.post<CalendarEventActionResponse>('/calendar/visibility', {
      viewer_user_id: viewerUserId,
      target_user_id: targetUserId,
    });
  }

  revokeVisibility(viewerUserId: number, targetUserId: number): Observable<CalendarEventActionResponse> {
    if (!this.hasValidToken()) {
      return throwError(() => new Error('Nincs érvényes token'));
    }
    const params = new HttpParams()
      .set('viewer_user_id', viewerUserId.toString())
      .set('target_user_id', targetUserId.toString());
    return this.delete<CalendarEventActionResponse>(`/calendar/visibility?viewer_user_id=${viewerUserId}&target_user_id=${targetUserId}`);
  }

  bulkGrantVisibility(viewerUserId: number, targetUserIds: number[]): Observable<CalendarEventActionResponse> {
    if (!this.hasValidToken()) {
      return throwError(() => new Error('Nincs érvényes token'));
    }
    return this.post<CalendarEventActionResponse>('/calendar/visibility/bulk', {
      viewer_user_id: viewerUserId,
      target_user_ids: targetUserIds,
    });
  }

  // ─── Egyeztetett cégek keresés ──────────────────

  getAgreedCompanies(search: string = '', limit: number = 5): Observable<{ success: boolean; companies: AgreedCompany[] }> {
    if (!this.hasValidToken()) {
      return this.returnErrorWhenNoToken();
    }
    let params = new HttpParams().set('limit', limit.toString());
    if (search) {
      params = params.set('search', search);
    }
    return this.get<{ success: boolean; companies: AgreedCompany[] }>('/calendar/agreed-companies', params);
  }

  getCompanyDetails(companyId: number): Observable<CompanyDetailsResponse> {
    if (!this.hasValidToken()) {
      return this.returnErrorWhenNoToken();
    }
    return this.get<CompanyDetailsResponse>(`/calendar/company-details/${companyId}`);
  }
}

export interface AgreedCompany {
  id: number;
  companyname: string;
  officials: string;
  display: string;
  cc_status: string;
  last_call_date: string | null;
}

export interface CompanyDetailsResponse {
  success: boolean;
  company: {
    id: number;
    companyname: string;
    taxnumber: string | null;
    companyregistrationnumber: string | null;
    address: string | null;
    mainactivity: string | null;
    county: string | null;
    cc_status: string | null;
    netrevenue: number | null;
    employeecount: number | null;
    phones: string[];
    emails: string[];
    last_call_notes: string | null;
  };
  officials: {
    id: number;
    fullname: string;
    phones: string;
    emails: string;
  }[];
}
